/**
 * Propose Part Change Modal — supplier proposes qty changes across one or
 * more open PO lines for a single part, in one transaction.
 *
 * Mounted from the supplier Stock Planning grid (call-off suppliers) and
 * the supplier Stock Levels page (all suppliers). Submission delegates to
 * the existing PO line propose-quantity-change workflow per line.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select';
import { Badge } from '../../ui/badge';
import { Loader2, AlertTriangle, ArrowDownNarrowWide, Sliders, Send } from 'lucide-react';
import { SUPPLIER_PROMISE_DATE } from '../../../config/dateLabels';
import { toast } from 'sonner';

import { partChangeAPI, purchasingAPI } from '../../../lib/api';
import type { OpenPOLine, ReasonCode } from '../../../lib/api';
import { getApiErrorMessage } from '../../../lib/api/types/common';

type AllocationMode = 'fifo' | 'manual';

interface ProposePartChangeModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  partNumber: string;
  partDescription?: string;
  customerId: number;
  /** Called after a successful submission so the host grid can refresh. */
  onSubmitted?: () => void;
}

/**
 * Editable proposed qty per line. Stored as string to avoid losing the
 * supplier's typed decimals when the value is re-rendered.
 */
type LineState = Record<number, string>;

const sumDecimal = (rows: { qty: string }[]): number =>
  rows.reduce((s, r) => {
    const n = parseFloat(r.qty);
    return s + (isNaN(n) ? 0 : n);
  }, 0);

export default function ProposePartChangeModal({
  open,
  onOpenChange,
  partNumber,
  partDescription,
  customerId,
  onSubmitted,
}: ProposePartChangeModalProps) {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [openLines, setOpenLines] = useState<OpenPOLine[]>([]);
  const [supplierId, setSupplierId] = useState<number | null>(null);
  const [proposed, setProposed] = useState<LineState>({});
  const [allocationMode, setAllocationMode] = useState<AllocationMode>('fifo');
  const [newTotal, setNewTotal] = useState<string>('');
  const [reasonCodes, setReasonCodes] = useState<ReasonCode[]>([]);
  const [reasonId, setReasonId] = useState<string>('');
  const [comments, setComments] = useState<string>('');

  // Reset + load on open
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setProposed({});
    setNewTotal('');
    setReasonId('');
    setComments('');
    setAllocationMode('fifo');

    Promise.all([
      partChangeAPI.getOpenPOLines({ part_number: partNumber, customer_id: customerId }),
      purchasingAPI.getReasonCodes({ category: 'quantity', is_active: true }),
    ])
      .then(([linesRes, reasonRes]) => {
        if (cancelled) return;
        const lines = linesRes.data.open_lines;
        setOpenLines(lines);
        setSupplierId(linesRes.data.supplier_id);
        const seedTotal = lines.reduce((s, l) => s + parseFloat(l.current_qty || '0'), 0);
        setNewTotal(seedTotal.toString());
        // Seed proposed = current for every line — FIFO redistribution kicks
        // in only once the supplier edits the new total.
        const seed: LineState = {};
        lines.forEach((l) => {
          seed[l.id] = l.current_qty;
        });
        setProposed(seed);
        setReasonCodes(reasonRes.data.results || []);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        toast.error(getApiErrorMessage(err, 'Failed to load open PO lines'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, partNumber, customerId]);

  // FIFO allocation.
  // - Decrease (target ≤ sum_of_currents): walk earliest → latest, draining
  //   each line up to its current qty until target is met. Tail lines drop
  //   to 0.
  // - Increase (target > sum_of_currents): keep each line at its current,
  //   then add the surplus to the earliest-due line (the one with the most
  //   pressing demand — FIFO uplift).
  // Switch to manual to override the default placement.
  const applyFifo = (target: number) => {
    let remaining = target;
    const next: LineState = {};
    for (const l of openLines) {
      const cap = parseFloat(l.current_qty || '0');
      const take = Math.max(0, Math.min(cap, remaining));
      next[l.id] = take.toString();
      remaining -= take;
    }
    if (remaining > 0 && openLines.length > 0) {
      const first = openLines[0];
      const firstCurrent = parseFloat(first.current_qty || '0');
      next[first.id] = (firstCurrent + remaining).toString();
    }
    setProposed(next);
  };

  const handleNewTotalChange = (val: string) => {
    setNewTotal(val);
    if (allocationMode === 'fifo') {
      const n = parseFloat(val);
      applyFifo(isNaN(n) ? 0 : n);
    }
  };

  const handleAllocationMode = (mode: AllocationMode) => {
    setAllocationMode(mode);
    if (mode === 'fifo') {
      const n = parseFloat(newTotal);
      applyFifo(isNaN(n) ? 0 : n);
    }
  };

  const handleLineQtyChange = (lineId: number, qty: string) => {
    setProposed((prev) => ({ ...prev, [lineId]: qty }));
  };

  const allocatedRows = useMemo(
    () => openLines.map((l) => ({ id: l.id, qty: proposed[l.id] ?? '0' })),
    [openLines, proposed],
  );
  const allocatedTotal = useMemo(() => sumDecimal(allocatedRows), [allocatedRows]);
  const targetTotal = parseFloat(newTotal) || 0;
  const totalsMatch = Math.abs(allocatedTotal - targetTotal) < 0.0005;

  // In manual mode the "new total" field is informational — it always
  // reflects whatever the per-line cells sum to. Keeps the supplier from
  // hitting a phantom mismatch warning when they're choosing per-line
  // values directly.
  useEffect(() => {
    if (allocationMode !== 'manual') return;
    const synced = allocatedTotal.toString();
    if (synced !== newTotal) setNewTotal(synced);
  }, [allocationMode, allocatedTotal, newTotal]);
  const originalTotal = useMemo(
    () => openLines.reduce((s, l) => s + parseFloat(l.current_qty || '0'), 0),
    [openLines],
  );

  // Lines whose proposed qty actually differs from current — only these spawn
  // PO line proposals. Used for the submit preview + payload.
  const changedLines = useMemo(() => {
    return openLines
      .map((l) => ({
        line: l,
        newQty: parseFloat(proposed[l.id] ?? (l.current_qty || '0')),
        currentQty: parseFloat(l.current_qty || '0'),
      }))
      .filter((c) => !isNaN(c.newQty) && c.newQty !== c.currentQty);
  }, [openLines, proposed]);

  const handleSubmit = async () => {
    if (!supplierId) {
      toast.error('Supplier context not resolved — refresh and try again.');
      return;
    }
    if (!totalsMatch) {
      toast.error(`Allocated total (${allocatedTotal}) must equal new total qty (${targetTotal}).`);
      return;
    }
    if (changedLines.length === 0) {
      toast.error('No changes to submit — adjust at least one line qty.');
      return;
    }
    if (!reasonId) {
      toast.error('Pick a reason for the change.');
      return;
    }
    const reason = reasonCodes.find((r) => String(r.id) === reasonId);
    if (!reason) {
      toast.error('Selected reason is no longer valid.');
      return;
    }

    setSubmitting(true);
    try {
      const res = await partChangeAPI.bulkProposeQty({
        part_number: partNumber,
        supplier_id: supplierId,
        customer_id: customerId,
        line_changes: changedLines.map((c) => ({
          po_line_id: c.line.id,
          new_qty: c.newQty.toString(),
        })),
        reason: `${reason.code} — ${reason.description}`,
        comments,
      });
      const { spawned_count, skipped_unchanged } = res.data;
      let msg = `${spawned_count} PO line proposal${spawned_count === 1 ? '' : 's'} submitted.`;
      if (skipped_unchanged.length > 0) {
        msg += ` (${skipped_unchanged.length} unchanged line${skipped_unchanged.length === 1 ? '' : 's'} skipped.)`;
      }
      toast.success(msg);
      onSubmitted?.();
      onOpenChange(false);
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, 'Failed to submit qty changes'), {
        duration: 8000,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (iso: string | null): string => {
    if (!iso) return '—';
    const d = new Date(iso);
    return isNaN(d.getTime()) ? iso : d.toLocaleDateString();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Propose qty change — <span className="text-primary">{partNumber}</span>
          </DialogTitle>
          <DialogDescription>
            {partDescription && <span>{partDescription} · </span>}
            {openLines.length} open PO line{openLines.length === 1 ? '' : 's'} ·{' '}
            <span className="font-mono">{originalTotal} units total</span>
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : openLines.length === 0 ? (
          <div className="py-8 text-center text-muted-foreground">
            No open PO lines for this part. There's nothing to change here.
          </div>
        ) : (
          <div className="space-y-5">
            {/* New total qty + allocation mode */}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="new-total" className="mb-1.5 block">
                  {allocationMode === 'fifo'
                    ? 'New total qty (across all open PO lines)'
                    : 'New total qty (live sum of per-line values)'}
                </Label>
                <div className="flex items-center gap-2">
                  <Input
                    id="new-total"
                    type="number"
                    min="0"
                    step="0.001"
                    value={newTotal}
                    onChange={(e) => handleNewTotalChange(e.target.value)}
                    disabled={allocationMode === 'manual'}
                    className="w-40"
                  />
                  <span className="text-sm text-muted-foreground">units</span>
                  <span className="ml-2 text-xs text-muted-foreground">
                    Currently <span className="font-semibold">{originalTotal}</span>
                  </span>
                </div>
              </div>
              <div>
                <Label className="mb-1.5 block">Allocation</Label>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant={allocationMode === 'fifo' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => handleAllocationMode('fifo')}
                    className="flex-1"
                  >
                    <ArrowDownNarrowWide className="h-4 w-4 mr-1.5" />
                    Earliest due first
                  </Button>
                  <Button
                    type="button"
                    variant={allocationMode === 'manual' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => handleAllocationMode('manual')}
                    className="flex-1"
                  >
                    <Sliders className="h-4 w-4 mr-1.5" />
                    Set per line
                  </Button>
                </div>
              </div>
            </div>

            {/* Per-line allocation table */}
            <div>
              <Label className="mb-2 block">Allocation across open PO lines</Label>
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50">
                    <tr className="text-xs uppercase tracking-wide text-muted-foreground">
                      <th className="text-left px-3 py-2 font-medium">PO</th>
                      <th className="text-left px-3 py-2 font-medium">Line</th>
                      <th className="text-left px-3 py-2 font-medium">{SUPPLIER_PROMISE_DATE}</th>
                      <th className="text-right px-3 py-2 font-medium">Current</th>
                      <th className="text-right px-3 py-2 font-medium">Proposed</th>
                      <th className="text-right px-3 py-2 font-medium">Δ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {openLines.map((l) => {
                      const current = parseFloat(l.current_qty || '0');
                      const prop = parseFloat(proposed[l.id] ?? (l.current_qty || '0'));
                      const delta = isNaN(prop) ? 0 : prop - current;
                      const deltaClass =
                        delta < 0 ? 'text-red-600' : delta > 0 ? 'text-green-600' : 'text-muted-foreground';
                      const hasPending =
                        l.acknowledge_status === 'change_proposed_supplier' ||
                        l.acknowledge_status === 'change_proposed_customer';
                      return (
                        <tr key={l.id} className="border-t">
                          <td className="px-3 py-2 font-medium">
                            {l.po_number}
                            {hasPending && (
                              <Badge variant="outline" className="ml-1.5 text-[10px]">
                                pending
                              </Badge>
                            )}
                          </td>
                          <td className="px-3 py-2">L{l.line_number}</td>
                          <td className="px-3 py-2 text-xs text-muted-foreground">
                            {formatDate(l.promise_date)}
                          </td>
                          <td className="px-3 py-2 text-right font-mono">{current}</td>
                          <td className="px-3 py-2 text-right">
                            <Input
                              type="number"
                              min="0"
                              step="0.001"
                              value={proposed[l.id] ?? '0'}
                              onChange={(e) => handleLineQtyChange(l.id, e.target.value)}
                              disabled={allocationMode === 'fifo'}
                              className="w-24 ml-auto text-right font-mono"
                            />
                          </td>
                          <td className={`px-3 py-2 text-right font-mono ${deltaClass}`}>
                            {delta === 0 ? '0' : delta > 0 ? `+${delta}` : delta}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot className="bg-muted/30 border-t">
                    <tr className="font-semibold">
                      <td colSpan={3} className="px-3 py-2 text-right text-xs uppercase tracking-wide text-muted-foreground">
                        Allocated total
                      </td>
                      <td className="px-3 py-2 text-right font-mono">{originalTotal}</td>
                      <td className="px-3 py-2 text-right font-mono">{allocatedTotal}</td>
                      <td className={`px-3 py-2 text-right font-mono ${
                        allocatedTotal - originalTotal === 0 ? 'text-muted-foreground' :
                        allocatedTotal - originalTotal > 0 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {allocatedTotal - originalTotal === 0 ? '0' :
                          allocatedTotal - originalTotal > 0 ? `+${allocatedTotal - originalTotal}` :
                          allocatedTotal - originalTotal}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
              {!totalsMatch && (
                <div className="mt-2 flex items-start gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-2.5">
                  <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <div>
                    Allocated total ({allocatedTotal}) doesn't match new total qty (
                    {targetTotal}). Adjust the per-line values or change the new total.
                  </div>
                </div>
              )}
            </div>

            {/* Reason + comments */}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="reason" className="mb-1.5 block">
                  Reason <span className="text-red-500">*</span>
                </Label>
                <Select value={reasonId} onValueChange={setReasonId}>
                  <SelectTrigger id="reason">
                    <SelectValue placeholder="Pick a reason…" />
                  </SelectTrigger>
                  <SelectContent>
                    {reasonCodes.map((r) => (
                      <SelectItem key={r.id} value={String(r.id)}>
                        {r.code} — {r.description}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="comments" className="mb-1.5 block">
                  Comments to customer
                </Label>
                <Textarea
                  id="comments"
                  value={comments}
                  onChange={(e) => setComments(e.target.value)}
                  rows={2}
                  placeholder="Add context: which lines, why, any alternatives offered…"
                />
              </div>
            </div>

            {/* Submit preview */}
            <div className="bg-muted/40 border rounded-lg p-3 text-sm">
              <div className="font-semibold mb-1.5">What happens on submit</div>
              <ul className="space-y-1 text-xs text-muted-foreground">
                <li>
                  •{' '}
                  <strong>{changedLines.length}</strong> PO line propos
                  {changedLines.length === 1 ? 'al' : 'als'} created (one per
                  changed line). Lines where qty is unchanged are skipped.
                </li>
                <li>• Customer notified to review &amp; accept/reject in the existing PO Change Requests tab.</li>
                <li>• Audit recorded against each PO line, with the reason &amp; your comments.</li>
                <li>• If any line fails validation, the whole submission rolls back.</li>
              </ul>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={
              loading ||
              submitting ||
              openLines.length === 0 ||
              !totalsMatch ||
              changedLines.length === 0 ||
              !reasonId
            }
          >
            {submitting ? (
              <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
            ) : (
              <Send className="h-4 w-4 mr-1.5" />
            )}
            Submit change request
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
